import { useCallback, useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import * as Y from "yjs";
import {
  Eye,
  FolderKanban,
  Check,
  RefreshCw,
  LayoutGrid,
  BarChart3,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuthStore } from "@/stores/auth-store";
import { useRedeemInviteLink } from "@/hooks/use-invite-links";
import { useTasks, type TaskFilters } from "@/hooks/use-tasks";
import { useProjectMembers } from "@/hooks/use-projects";
import { PublicProjectReaderProvider } from "@/lib/public-project-reader";
import { AppBrand } from "@/components/layout/app-sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { TaskTable } from "@/components/tasks/task-table";
import { TaskDetailPanel } from "@/components/tasks/task-detail-panel";
import { TaskFiltersBar } from "@/components/tasks/task-filters";
import { KanbanView } from "@/components/tasks/kanban/kanban-view";
import { CalendarView } from "@/components/tasks/calendar/calendar-view";
import { GanttView } from "@/components/tasks/gantt/gantt-view";
import { DocumentEditor } from "@/components/tasks/document/document-editor";
import {
  DocumentPersonMentionPopover,
  type DocumentPersonMentionTarget,
} from "@/components/tasks/document/document-person-mention-popover";
import { DocumentToolbar } from "@/components/tasks/document/document-toolbar";
import { viewModes } from "@/components/ai/view-modes";
import type {
  CustomField,
  SavedView,
  Task,
  TaskStatus,
  TaskPriority,
} from "@/lib/schemas";

interface SharedProject {
  members: { id: string; name: string; avatarUrl: string | null }[];
  project: {
    id: string;
    name: string;
    description: string | null;
    color: string;
    iconEmoji: string;
    imageUrl: string | null;
  };
  views: SavedView[];
  customFields: CustomField[];
}
function viewFilters(view: SavedView | null): TaskFilters {
  const filters = view?.config.filters;
  return filters
    ? {
        status: filters.status as TaskStatus[] | undefined,
        priority: filters.priority as TaskPriority[] | undefined,
        assignee: filters.assignees?.[0],
        dueDateFrom: filters.dueDateFrom,
        dueDateTo: filters.dueDateTo,
        search: filters.search,
        customFields: filters.customFields,
      }
    : {};
}
function PublicDocument({
  token,
  view,
  projectId,
  onTask,
}: {
  token: string;
  view: SavedView;
  projectId: string;
  onTask: (id: string) => void;
}) {
  const { t } = useI18n();
  const [doc] = useState(() => new Y.Doc());
  const [, setEditorReady] = useState(false);
  const handleReady = useCallback(() => setEditorReady(true), []);
  const [personTarget, setPersonTarget] =
    useState<DocumentPersonMentionTarget | null>(null);
  const personAnchor = useRef<HTMLElement | null>(null);
  useEffect(() => () => doc.destroy(), [doc]);
  const snapshot = useQuery({
    queryKey: ["public-document-snapshot", token, view.id],
    refetchInterval: 10000,
    retry: false,
    queryFn: async () => {
      const response = await fetch(
        `/api/public/projects/${encodeURIComponent(token)}/documents/${view.id}/snapshot`,
        { referrerPolicy: "no-referrer" },
      );
      if (!response.ok) throw new Error("Document unavailable");
      return new Uint8Array(await response.arrayBuffer());
    },
  });
  const members = useProjectMembers(projectId);
  const tasks = useTasks(projectId, { includeSubtasks: true });
  const roomName = "public:" + view.id;
  if (snapshot.error) return <p role="alert">{snapshot.error.message}</p>;
  if (!snapshot.data)
    return <p className="p-6 text-foreground-muted">{t("common.loading")}</p>;
  return (
    <div
      className="flex flex-col gap-2 sm:gap-3"
      data-testid="public-document-view"
    >
      <div className="flex items-center justify-end text-xs text-foreground-muted gap-1">
        <Eye className="h-3.5 w-3.5" />
        {t("document.readOnlyBadge")}
      </div>
      <DocumentToolbar
        roomName={roomName}
        connectionStatus="disconnected"
        peers={[]}
        lastSavedAt={null}
        saveStatus="saved"
        viewName={view.name}
        readOnly
        mentionTasks={tasks.data?.tasks ?? []}
        mentionMembers={members.data ?? []}
      />
      <DocumentEditor
        roomName={roomName}
        projectId={projectId}
        doc={doc}
        provider={null}
        snapshot={snapshot.data}
        user={{ name: "Viewer", color: "#6366f1" }}
        editable={false}
        onReady={handleReady}
        onPersonMentionClick={(id, label, anchor) => {
          personAnchor.current = anchor ?? null;
          setPersonTarget({
            id,
            label,
            avatarUrl:
              members.data?.find((m) => m.userId === id)?.user.avatarUrl ??
              null,
          });
        }}
        onMentionClick={onTask}
        mentionTasks={tasks.data?.tasks ?? []}
        mentionMembers={members.data}
      />
      <DocumentPersonMentionPopover
        projectId={projectId}
        target={personTarget}
        anchorRef={personAnchor}
        open={!!personTarget}
        onOpenChange={(open) => {
          if (!open) setPersonTarget(null);
        }}
        onOpenTask={onTask}
      />
    </div>
  );
}
export function PublicProjectPage() {
  const { token = "" } = useParams();
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ["public-project", token],
    queryFn: () =>
      apiGet<SharedProject>(`/public/projects/${encodeURIComponent(token)}`),
    retry: false,
    refetchInterval: 10000,
  });
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.append(meta);
    return () => meta.remove();
  }, []);
  if (query.error)
    return (
      <div className="m-auto max-w-lg p-8 text-center">
        <Eye className="mx-auto mb-4 text-foreground-muted" />
        <p role="alert">{query.error.message}</p>
      </div>
    );
  if (!query.data) return <p className="p-6">{t("common.loading")}</p>;
  return (
    <PublicProjectReaderProvider
      value={{ token, projectId: query.data.project.id }}
    >
      <PublicProjectContents token={token} data={query.data} />
    </PublicProjectReaderProvider>
  );
}
function PublicProjectContents({
  token,
  data,
}: {
  token: string;
  data: SharedProject;
}) {
  const { t, language } = useI18n();
  const fr = language === "fr";
  const queryClient = useQueryClient();
  const authenticated = useAuthStore((state) => !!state.user);
  const join = useRedeemInviteLink();
  const [mode, setMode] = useState("TABLE");
  const [viewId, setViewId] = useState(
    data.views.find((v) => v.type === "TABLE" && v.isDefault)?.id ?? "",
  );
  const active =
    data.views.find((v) => v.id === viewId && v.type === mode) ?? null;
  const [filters, setFilters] = useState<TaskFilters>(() =>
    viewFilters(active),
  );
  const [taskId, setTaskId] = useState<string | null>(null);
  const tasks = useTasks(data.project.id, filters);
  const members = useProjectMembers(data.project.id);
  const allTasks = tasks.data?.tasks ?? [];
  const { hasNextPage, isFetching, fetchNextPage } = tasks;
  useEffect(() => {
    if (hasNextPage && !isFetching && allTasks.length < 10000)
      void fetchNextPage();
  }, [hasNextPage, isFetching, fetchNextPage, allTasks.length]);
  const chooseView = (view: SavedView | null) => {
    setViewId(view?.id ?? "");
    setFilters(viewFilters(view));
  };
  const chooseMode = (type: string) => {
    setMode(type);
    chooseView(
      data.views.find(
        (v) => v.type === type && (v.isDefault || type === "DOCUMENT"),
      ) ?? null,
    );
  };
  const selectTask = (task: Task) => setTaskId(task.id);
  const projectImage = (
    <span
      className="h-8 w-8 sm:h-10 sm:w-10 flex shrink-0 items-center justify-center rounded-lg text-lg sm:text-xl"
      style={{ backgroundColor: data.project.color || "#6366f1" }}
    >
      {data.project.imageUrl ? (
        <img
          src={data.project.imageUrl}
          alt=""
          className="h-full w-full rounded-lg object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        data.project.iconEmoji
      )}
    </span>
  );
  return (
    <div className="h-dvh flex overflow-hidden">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/10 bg-white/5 backdrop-blur-md overflow-y-auto">
        <div className="h-16 px-5 flex items-center border-b border-white/10">
          <AppBrand />
        </div>
        <div className="p-3 space-y-6">
          <div className="rounded-lg px-3 py-2.5 text-primary bg-primary/10 flex gap-3 items-center">
            <FolderKanban className="h-4 w-4" />
            <span className="text-sm font-medium">
              {fr ? "Projet partagé" : "Shared project"}
            </span>
          </div>
          <div>
            <p className="px-3 mb-2 text-xs uppercase font-medium text-foreground-muted">
              {fr ? "Projet actuel" : "Current project"}
            </p>
            <div className="flex items-center gap-3 rounded-xl bg-white/10 px-3 py-3">
              {projectImage}
              <p className="truncate text-sm font-semibold">
                {data.project.name}
              </p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-5">
            <p className="px-3 mb-3 text-xs uppercase tracking-wider text-foreground-muted">
              {mode === "DOCUMENT"
                ? "Documents"
                : fr
                  ? "Vues sauvegardées"
                  : "Saved views"}
            </p>
            {mode !== "DOCUMENT" && (
              <button
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm ${!active ? "bg-primary/10 text-primary" : "text-foreground-secondary hover:bg-white/5"}`}
                onClick={() => chooseView(null)}
              >
                {fr ? "Toutes les tâches" : "All tasks"}
                {!active && <Check className="h-4 w-4" />}
              </button>
            )}
            {data.views
              .filter((v) => v.type === mode)
              .map((view) => {
                const Icon =
                  viewModes.find((m) => m.type === mode)?.icon ?? Eye;
                return (
                  <button
                    key={view.id}
                    onClick={() => chooseView(view)}
                    className={`mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-left ${active?.id === view.id ? "bg-primary/10 text-primary" : "text-foreground-secondary hover:bg-white/5"}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{view.name}</span>
                    {active?.id === view.id && <Check className="h-4 w-4" />}
                  </button>
                );
              })}
          </div>
        </div>
        <div className="mt-auto border-t border-white/10 p-4 text-xs text-foreground-muted flex gap-2 items-center">
          <Eye className="h-4 w-4" />
          {fr ? "Accès en lecture seule" : "Read-only access"}
        </div>
      </aside>
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 shrink-0 border-b border-white/10 bg-white/5 backdrop-blur-md">
          {projectImage}
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-white truncate">
              {data.project.name}
            </h1>
            {data.project.description && (
              <p className="text-sm text-white/50 truncate hidden sm:block">
                {data.project.description}
              </p>
            )}
          </div>
          <div className="hidden sm:flex -space-x-2">
            {data.members.slice(0, 6).map((member) => (
              <Avatar
                key={member.id}
                className="h-8 w-8 border-2 border-surface"
                title={member.name}
              >
                <AvatarImage src={member.avatarUrl ?? undefined} />
                <AvatarFallback className="bg-primary/20 text-xs">
                  {member.name
                    .split(" ")
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          <span className="flex items-center gap-1 text-xs text-foreground-muted">
            <Eye className="h-4 w-4" />
            {fr ? "Lecture seule" : "Read only"}
          </span>
          {authenticated ? (
            <Button
              size="sm"
              disabled={join.isPending}
              onClick={() =>
                join.mutate(token, {
                  onSuccess: (r) =>
                    window.location.assign(`/projects/${r.projectId}`),
                })
              }
            >
              {fr ? "Rejoindre comme lecteur" : "Join as viewer"}
            </Button>
          ) : (
            <Button size="sm" variant="outline" asChild>
              <Link
                to={`/login?redirect=${encodeURIComponent(`/public-project/${token}`)}`}
              >
                {fr ? "Se connecter pour rejoindre" : "Sign in to join"}
              </Link>
            </Button>
          )}
          {join.error && <p role="alert">{join.error.message}</p>}
        </header>
        <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <h2 className="text-lg font-semibold text-foreground">
              {mode === "DOCUMENT" ? "Documents" : t("tasks.title")}
              <span className="ml-3 text-sm font-normal text-foreground-muted">
                {mode === "DOCUMENT"
                  ? ""
                  : `${tasks.data?.total ?? 0} ${t("tasks.task")}${(tasks.data?.total ?? 0) !== 1 ? "s" : ""}`}
              </span>
            </h2>
            <div
              role="tablist"
              aria-label={t("viewType.toggleAriaLabel")}
              className="inline-flex items-center max-w-full overflow-x-auto whitespace-nowrap shrink-0 rounded-md border border-white/10 bg-white/5 p-0.5"
            >
              {viewModes.map((item) => (
                <button
                  key={item.type}
                  role="tab"
                  aria-selected={mode === item.type}
                  onClick={() => chooseMode(item.type)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${mode === item.type ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground-muted hover:text-foreground"}`}
                >
                  <>
                    {item.type === "TABLE" ? (
                      <LayoutGrid className="h-3.5 w-3.5" />
                    ) : item.type === "GANTT" ? (
                      <BarChart3 className="h-3.5 w-3.5" />
                    ) : (
                      <item.icon className="h-3.5 w-3.5" />
                    )}
                  </>
                  {fr ? item.fr : item.en}
                </button>
              ))}
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="ml-auto"
              aria-label={fr ? "Actualiser" : "Refresh"}
              onClick={() =>
                void Promise.all([
                  queryClient.invalidateQueries({
                    queryKey: ["public-project-reader", token],
                  }),
                  queryClient.invalidateQueries({
                    queryKey: ["public-project", token],
                  }),
                  queryClient.invalidateQueries({
                    queryKey: ["public-document-snapshot", token],
                  }),
                ])
              }
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {mode !== "DOCUMENT" && (
            <div className="mb-6">
              <TaskFiltersBar
                filters={filters}
                onFiltersChange={setFilters}
                members={members.data ?? []}
                customFields={data.customFields}
              />
            </div>
          )}
          {mode === "DOCUMENT" ? (
            active ? (
              <PublicDocument
                key={active.id}
                token={token}
                view={active}
                projectId={data.project.id}
                onTask={setTaskId}
              />
            ) : (
              <p className="text-foreground-muted">
                {fr
                  ? "Sélectionnez un document partagé."
                  : "Select a shared document."}
              </p>
            )
          ) : tasks.error ? (
            <p role="alert">{tasks.error.message}</p>
          ) : mode === "KANBAN" ? (
            <KanbanView
              projectId={data.project.id}
              tasks={allTasks}
              isLoading={tasks.isLoading}
              customFields={data.customFields}
              visibleCardFieldIds={active?.visibleCardFields ?? []}
              activeView={active}
              onCardClick={selectTask}
              onSelectSubtask={selectTask}
              canEdit={false}
              projectMembers={members.data}
            />
          ) : mode === "CALENDAR" ? (
            <CalendarView
              projectId={data.project.id}
              tasks={allTasks}
              isLoading={tasks.isLoading}
              customFields={data.customFields}
              activeView={active}
              onTaskClick={selectTask}
              canEdit={false}
            />
          ) : mode === "GANTT" ? (
            <GanttView
              projectId={data.project.id}
              tasks={allTasks}
              isLoading={tasks.isLoading}
              customFields={data.customFields}
              activeView={active}
              onTaskClick={selectTask}
              canEdit={false}
            />
          ) : (
            <TaskTable
              projectId={data.project.id}
              tasks={allTasks}
              onEditTask={selectTask}
              onTaskClick={selectTask}
              onDeleteTask={() => {}}
              onStatusChange={() => {}}
              onPriorityChange={() => {}}
              selectedTasks={[]}
              canEdit={false}
              columnOrder={active?.config.columnOrder}
              hiddenColumns={active?.config.hiddenColumns}
              customFields={data.customFields}
              projectMembers={members.data}
            />
          )}
        </div>
      </main>
      {taskId && (
        <TaskDetailPanel
          taskId={taskId}
          projectId={data.project.id}
          onClose={() => setTaskId(null)}
          onEdit={() => {}}
          onSubtaskSelect={selectTask}
          canEdit={false}
        />
      )}
    </div>
  );
}
